import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await resetPassword(email);
    setLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      setSent(true);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 relative overflow-hidden bg-background">
      {/* Left side: Form */}
      <div className="flex flex-col justify-center items-center p-8 lg:p-12 relative z-10">
        <div className="w-full max-w-sm space-y-8">
          
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/logo.svg" alt="Koin Logo" className="h-16 w-auto" />
          </div>
          
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">Reset Password</h1>
            <p className="text-muted-foreground">Enter your email to receive a reset link</p>
          </div>

          {sent ? (
            <div className="text-center space-y-6">
              <p className="text-muted-foreground">Check your email for a password reset link.</p>
              <Link to="/login" className="inline-block text-primary font-medium hover:underline transition-colors">
                Back to login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="you@example.com" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                  className="h-11"
                />
              </div>
              
              <Button type="submit" className="w-full h-11 text-base font-medium transition-all" disabled={loading}>
                {loading ? 'Sending…' : 'Send Reset Link'}
              </Button>
              
              <div className="text-center">
                <Link to="/login" className="text-sm text-primary font-medium hover:underline transition-colors">
                  Back to login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Right side: Decorative */}
      <div className="hidden lg:flex flex-col justify-center items-center bg-primary/5 relative overflow-hidden border-l border-border/40">
         {/* Blur effects & gradients */}
         <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-background" />
         <div className="absolute -top-1/4 -right-1/4 w-[800px] h-[800px] bg-primary/20 rounded-full blur-[100px] opacity-70 pointer-events-none" />
         <div className="absolute -bottom-1/4 -left-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[80px] opacity-70 pointer-events-none" />
         
         {/* Logo Showcase */}
         <div className="relative z-10 flex flex-col items-center max-w-lg text-center px-12">
            <div className="p-4 bg-background/50 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 mb-10">
              <img src="/logo.svg" alt="Koin Logo" className="h-32 w-auto drop-shadow-xl" />
            </div>
            <h2 className="text-4xl font-bold tracking-tight mb-4 text-foreground/90">
              Secure Account Access
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              We ensure your data is always protected. Easily recover your account and get back to managing your finances safely.
            </p>
         </div>
      </div>
    </div>
  );
}
